import { z } from "zod";
import { pageOf, PageQuerySchema } from "../common/pagination.js";
import { IdSchema, IsoDateTimeSchema, LocaleSchema } from "../common/primitives.js";
const AssignableRoleKeySchema = z.union([
  z.enum(["admin", "project_manager", "engineer", "draftsman", "accountant"]),
  /** A custom role of the organisation (ORGX-BE-01). */
  z.string().regex(/^custom_[a-z0-9]{8,32}$/)
]);
const MembershipStatusSchema = z.enum(["invited", "active", "suspended", "deactivated"]);
const InvitationStatusSchema = z.enum(["pending", "accepted", "revoked", "expired"]);
const InvitationTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const CreateInvitationRequestSchema = z.strictObject({
  email: z.email().max(320),
  roleKey: AssignableRoleKeySchema,
  /** Language of the email; the organisation's default when absent. */
  locale: LocaleSchema.optional()
});
const InvitationSchema = z.strictObject({
  id: IdSchema,
  email: z.string(),
  roleKey: z.string(),
  status: InvitationStatusSchema,
  expiresAt: IsoDateTimeSchema,
  invitedBy: z.strictObject({ id: IdSchema, displayName: z.string() }),
  lastSentAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema
});
const InvitationListSchema = z.strictObject({ items: z.array(InvitationSchema).max(500) });
const InvitationParamsSchema = z.strictObject({ orgId: IdSchema, invitationId: IdSchema });
const InvitationTokenRequestSchema = z.strictObject({ token: InvitationTokenSchema });
const InvitationPreviewSchema = z.strictObject({
  organizationName: z.string(),
  roleKey: z.string(),
  /** The address the invitation was sent to. */
  email: z.string(),
  status: InvitationStatusSchema,
  expiresAt: IsoDateTimeSchema,
  /** The signed-in user's verified email is the invited one (acceptance possible). */
  emailMatches: z.boolean()
});
const AcceptInvitationResponseSchema = z.strictObject({ organizationId: IdSchema });
const MemberActionSchema = z.enum(["change_role", "deactivate", "transfer_ownership"]);
const MemberSchema = z.strictObject({
  /** Membership id (the member's id in this organisation). */
  id: IdSchema,
  userId: IdSchema,
  displayName: z.string(),
  email: z.string(),
  roleKey: z.string(),
  status: MembershipStatusSchema,
  joinedAt: IsoDateTimeSchema.nullable(),
  lastActiveAt: IsoDateTimeSchema.nullable(),
  /** Optimistic-locking version, sent back as If-Match: W/"<version>" to change the member. */
  version: z.int().min(1),
  _permissions: z.array(MemberActionSchema)
});
const MemberListQuerySchema = PageQuerySchema.extend({
  status: MembershipStatusSchema.optional(),
  roleKey: z.string().min(1).max(64).optional(),
  q: z.string().trim().min(1).max(100).optional()
});
const MemberListSchema = pageOf(MemberSchema);
const MemberParamsSchema = z.strictObject({ orgId: IdSchema, memberId: IdSchema });
const ChangeRoleRequestSchema = z.strictObject({ roleKey: AssignableRoleKeySchema });
const TransferItemSchema = z.strictObject({
  provider: z.string().min(1).max(64),
  id: z.string().min(1).max(100),
  /** What it is, for the wizard (e.g. task title, project name). */
  label: z.string().max(300)
});
const TransferPlanSchema = z.strictObject({ items: z.array(TransferItemSchema).max(1e3) });
const DeactivateMemberRequestSchema = z.strictObject({
  /** Receives every item not listed in `assignments`. */
  defaultAssigneeId: IdSchema.optional(),
  assignments: z.array(
    z.strictObject({
      provider: z.string().min(1).max(64),
      itemId: z.string().min(1).max(100),
      /** Membership id of the member who takes the item over. */
      assigneeId: IdSchema
    })
  ).max(1e3).default([])
});
const OwnerTransferStatusSchema = z.enum(["pending", "accepted", "cancelled", "expired"]);
const OwnerTransferSchema = z.strictObject({
  id: IdSchema,
  fromMemberId: IdSchema,
  toMemberId: IdSchema,
  toDisplayName: z.string(),
  status: OwnerTransferStatusSchema,
  expiresAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema
});
const OwnerTransferStateSchema = z.strictObject({
  transfer: OwnerTransferSchema.nullable()
});
const ProposeOwnerTransferRequestSchema = z.strictObject({ toMemberId: IdSchema });
export * from "./roles.js";
export {
  AcceptInvitationResponseSchema,
  AssignableRoleKeySchema,
  ChangeRoleRequestSchema,
  CreateInvitationRequestSchema,
  DeactivateMemberRequestSchema,
  InvitationListSchema,
  InvitationParamsSchema,
  InvitationPreviewSchema,
  InvitationSchema,
  InvitationStatusSchema,
  InvitationTokenRequestSchema,
  InvitationTokenSchema,
  MemberActionSchema,
  MemberListQuerySchema,
  MemberListSchema,
  MemberParamsSchema,
  MemberSchema,
  MembershipStatusSchema,
  OwnerTransferSchema,
  OwnerTransferStateSchema,
  OwnerTransferStatusSchema,
  ProposeOwnerTransferRequestSchema,
  TransferItemSchema,
  TransferPlanSchema
};
//# sourceMappingURL=index.js.map